import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { WelcomeTourFeature, WelcomeTourStep, welcomeTourSteps } from './welcome-tour-features';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-welcome-tour',
  templateUrl: './welcome-tour.html',
  styleUrl: './welcome-tour.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WelcomeTour {
  readonly features = input<WelcomeTourFeature[]>([]);
  readonly finished = output<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly popover = viewChild<ElementRef<HTMLElement>>('popover');
  private frame = 0;
  private focusedStep = -1;

  readonly step = signal(0);
  readonly targetRect = signal<SpotlightRect | null>(null);

  readonly steps = computed(() => welcomeTourSteps(this.features()));
  readonly current = computed<WelcomeTourStep>(() => this.steps()[this.step()] ?? this.steps()[0]);
  readonly isLastStep = computed(() => this.step() >= this.steps().length - 1);
  readonly showBack = computed(() => this.step() > 0);
  readonly cardStyle = computed(() => this.placeCard(this.targetRect()));

  constructor() {
    const loop = () => {
      this.measureTarget();
      this.frame = requestAnimationFrame(loop);
    };
    this.frame = requestAnimationFrame(loop);
    this.destroyRef.onDestroy(() => cancelAnimationFrame(this.frame));
  }

  back(): void {
    this.step.update((current) => Math.max(0, current - 1));
  }

  next(): void {
    if (this.isLastStep()) {
      this.finished.emit();
      return;
    }
    this.step.update((current) => current + 1);
  }

  skip(): void {
    this.finished.emit();
  }

  hold(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  advanceFromTarget(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.next();
  }

  private measureTarget(): void {
    const step = this.current();
    const element = this.findTarget(step);
    const next = element ? this.toRect(element.getBoundingClientRect()) : null;
    const previous = this.targetRect();
    if (!this.sameRect(previous, next)) {
      this.targetRect.set(next);
    }
    if (this.focusedStep !== this.step()) {
      this.focusedStep = this.step();
      this.popover()?.nativeElement.querySelector<HTMLButtonElement>('[data-tour-next]')?.focus();
    }
  }

  private findTarget(step: WelcomeTourStep): Element | null {
    if (typeof document === 'undefined') {
      return null;
    }
    if (step.kind === 'welcome') {
      return document.querySelector('.welcome-text');
    }
    if (step.kind === 'menu') {
      return document.querySelector('aside.sidebar');
    }
    const label = step.targetLabel;
    if (!label) {
      return null;
    }
    return (
      Array.from(document.querySelectorAll('a.menu-link')).find((link) =>
        (link.textContent ?? '').replace(/\s+/g, ' ').trim().includes(label),
      ) ?? null
    );
  }

  private toRect(rect: DOMRect): SpotlightRect {
    return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
  }

  private sameRect(previous: SpotlightRect | null, next: SpotlightRect | null): boolean {
    if (previous === next) {
      return true;
    }
    if (!previous || !next) {
      return false;
    }
    return (
      previous.top === next.top &&
      previous.left === next.left &&
      previous.width === next.width &&
      previous.height === next.height
    );
  }

  private placeCard(rect: SpotlightRect | null): Record<string, string> {
    if (!rect || typeof window === 'undefined') {
      return { top: '1rem', left: '1rem', width: 'min(22rem, calc(100vw - 2rem))' };
    }
    const gap = 16;
    const width = 352;
    const estimatedHeight = 220;
    const spaceRight = window.innerWidth - (rect.left + rect.width);
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    if (spaceRight >= width + gap) {
      const top = Math.max(12, Math.min(rect.top, window.innerHeight - estimatedHeight - 12));
      return { top: `${top}px`, left: `${rect.left + rect.width + gap}px`, width: `${width}px` };
    }
    if (spaceBelow >= 140) {
      return {
        top: `${rect.top + rect.height + gap}px`,
        left: '1rem',
        right: '1rem',
        width: 'auto',
      };
    }
    return { bottom: '1rem', left: '1rem', right: '1rem', width: 'auto' };
  }
}
